import React, { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap";

// Custom Components
import TableContainer from "components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import GlobalInputSearch from "components/Common/GlobalInputSearch";

// Custom Hooks
import {
  useMovementTypes,
  useCreateMovementType,
  useEditMovementType,
  useDeleteMovementType,
} from "hooks/useMovementTypes";
import { useListParams } from "hooks/useListParams";

// Utils
import { formatReadableDate } from "utils/formatDate";

const isCustomerDeliveryMovementType = movementType => {
  const slug = movementType && movementType.slug;
  const name = movementType && movementType.name;

  return slug === "customer-delivery" || name === "Customer Delivery";
};

const MovementTypeList = () => {
  document.title = "Movement Types List";

  // State
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [movementType, setMovementType] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  // URL search params hook
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" });

  // Query params for API
  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  );

  // React Query hooks
  const {
    data: movementTypesResponse,
    isLoading,
    refetch: refetchMovementTypes,
    isRefetching,
  } = useMovementTypes(queryParams);

  const { mutateAsync: createMovementType, isPending: createLoading } =
    useCreateMovementType();
  const { mutateAsync: editMovementType, isPending: editLoading } =
    useEditMovementType();
  const { mutateAsync: deleteMovementType, isPending: deleteLoading } =
    useDeleteMovementType();

  // Toggle modal
  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false);
      setMovementType(null);
      setIsEdit(false);
    } else setModal(true);
  }, [modal]);

  // Handle add new
  const handleAddNewClick = useCallback(() => {
    setMovementType(null);
    setIsEdit(false);
    toggleModal();
  }, [toggleModal]);

  // Handle edit
  const handleEditClick = useCallback(
    (item) => {
      setMovementType(item);
      setIsEdit(true);
      toggleModal();
    },
    [toggleModal]
  );

  // Handle delete
  const handleDeleteClick = useCallback((item) => {
    setMovementType(item);
    setDeleteModal(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!movementType?.id) return;
    try {
      const res = await deleteMovementType(movementType.id);
      toast.success(res?.message ?? "Movement type deleted successfully.");
      setDeleteModal(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete movement type.");
    }
  }, [movementType, deleteMovementType]);

  // Refresh list
  const handleRefresh = () => refetchMovementTypes();

  // Formik validation
  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters")
      .required("Please enter movement type name"),
  });

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: values.name };
      const action = isEdit
        ? editMovementType({ id: movementType.id, ...payload })
        : createMovementType(payload);

      try {
        const res = await action;
        toast.success(
          res?.message ?? `Movement type ${isEdit ? "updated" : "created"} successfully.`
        );
        resetForm();
        toggleModal();
      } catch (error) {
        console.error(error);
        toast.error("Failed to save movement type.");
      }
    },
    [isEdit, movementType, editMovementType, createMovementType, toggleModal]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { name: movementType?.name || "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  });

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const type = row.original;
          const isLocked = isCustomerDeliveryMovementType(type);

          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              {isLocked ? (
                <li>
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-secondary"
                    disabled
                    id={`lockedtooltip-${type.id}`}
                  >
                    <i className="mdi mdi-lock-outline" />
                    <UncontrolledTooltip
                      placement="top"
                      target={`lockedtooltip-${type.id}`}
                    >
                      Customer Delivery is locked
                    </UncontrolledTooltip>
                  </button>
                </li>
              ) : (
                <>
                  <li>
                    <button
                      type="button"
                      className="btn btn-sm btn-soft-info"
                      onClick={() => handleEditClick(type)}
                      id={`edittooltip-${type.id}`}
                    >
                      <i className="mdi mdi-pencil-outline" />
                      <UncontrolledTooltip
                        placement="top"
                        target={`edittooltip-${type.id}`}
                      >
                        Edit
                      </UncontrolledTooltip>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="btn btn-sm btn-soft-danger"
                      onClick={() => handleDeleteClick(type)}
                      id={`deletetooltip-${type.id}`}
                    >
                      <i className="mdi mdi-delete-outline" />
                      <UncontrolledTooltip
                        placement="top"
                        target={`deletetooltip-${type.id}`}
                      >
                        Delete
                      </UncontrolledTooltip>
                    </button>
                  </li>
                </>
              )}
            </ul>
          );
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  const data = movementTypesResponse?.data?.data || [];
  const paginationInfo = movementTypesResponse?.data;

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Movement Type" breadcrumbItem="Movement Type List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">
                      Movement Type List
                    </h5>
                    <div className="flex-shrink-0">
                      <Button color="primary" onClick={handleAddNewClick}>
                        Add New
                      </Button>
                      <Button
                        color="light"
                        onClick={handleRefresh}
                        className="ms-1"
                      >
                        <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="ms-1"
                        >
                          Reset Filters
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row>
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={data}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || isRefetching}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Modal for Add/Edit */}
          <Modal isOpen={modal} toggle={toggleModal} centered>
            <ModalHeader toggle={toggleModal}>
              {isEdit ? "Edit Movement Type" : "Add Movement Type"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter movement type name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    invalid={formik.touched.name && !!formik.errors.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                  )}
                </div>
                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={editLoading || createLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Movement Type"}
                    {(editLoading || createLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default MovementTypeList;
